import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

export interface Project {
  id: string;
  title: string;
  description: string;
  status: string;
  category: string;
  owner: any;
  milestones: any[];
  goals: any[];
  votingSessions: any[];
  files: any[];
  stats: any;
  geographicRegion?: {
    parish: string;
    country: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  comments?: any[];
  [key: string]: any;
}

interface ProjectsState {
  items: Record<string, Project>;
  list: {
    items: Project[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
    filters: {
      category: string;
      status: string;
      parish: string;
      search: string;
      sortBy: string;
      sortOrder: string;
    };
    isLoading: boolean;
    error: string | null;
  };
  currentProject: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ProjectsState = {
  items: {},
  list: {
    items: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 0,
    },
    filters: {
      category: '',
      status: '',
      parish: '',
      search: '',
      sortBy: 'date',
      sortOrder: 'desc',
    },
    isLoading: false,
    error: null,
  },
  currentProject: null,
  isLoading: false,
  error: null,
};

export const fetchProjects = createAsyncThunk(
  'projects/fetchList',
  async (filters: any, { getState }) => {
    const state = getState() as { projects: ProjectsState };
    const params = { ...state.projects.list.filters, ...filters };
    const response = await axios.get(`${API_BASE_URL}/projects`, { params });
    return response.data.data;
  }
);

export const fetchProjectById = createAsyncThunk(
  'projects/fetchById',
  async (id: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const config = state.auth.accessToken
      ? { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
      : {};
    const response = await axios.get(`${API_BASE_URL}/projects/${id}`, config);
    return response.data.data.project;
  }
);

export const createProject = createAsyncThunk(
  'projects/create',
  async (data: any, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.post(
      `${API_BASE_URL}/projects`,
      data,
      { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
    );
    return response.data.data.project;
  }
);

export const updateProject = createAsyncThunk(
  'projects/update',
  async ({ id, data }: { id: string; data: any }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.put(
      `${API_BASE_URL}/projects/${id}`,
      data,
      { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
    );
    return response.data.data.project;
  }
);

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ProjectsState['list']['filters']>>) => {
      state.list.filters = { ...state.list.filters, ...action.payload };
    },
    setCurrentProject: (state, action: PayloadAction<string | null>) => {
      state.currentProject = action.payload;
    },
    updateProjectFromWS: (state, action: PayloadAction<Partial<Project> & { id: string }>) => {
      if (state.items[action.payload._id]) {
        state.items[action.payload._id] = { ...state.items[action.payload._id], ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.list.isLoading = true;
        state.list.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.list.isLoading = false;
        state.list.items = action.payload.projects;
        state.list.pagination = action.payload.pagination;
        // Update items cache
        action.payload.projects.forEach((project: Project) => {
          state.items[project._id] = project;
        });
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.list.isLoading = false;
        state.list.error = action.error.message || 'Failed to fetch projects';
      })
      .addCase(fetchProjectById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items[action.payload._id] = action.payload;
        state.currentProject = action.payload._id;
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch project';
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.items[action.payload._id] = action.payload;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.items[action.payload._id] = action.payload;
      });
  },
});

export const { setFilters, setCurrentProject, updateProjectFromWS } = projectsSlice.actions;
export default projectsSlice.reducer;

